import React, {
  ReactNode,
  useState,
  useEffect,
} from 'react';

import moment from 'moment';
import {
  Grid,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Box,
  useTheme,
  InputBaseComponentProps,
  Tooltip,
} from '@material-ui/core';

import {
  Error as ErrorIcon,
} from '@material-ui/icons';

import {
  KeyboardDatePicker,
  KeyboardTimePicker,
  KeyboardDateTimePicker,
} from '@material-ui/pickers';
import { MaterialUiPickersDate } from '@material-ui/pickers/typings/date';
import { ParsableDate } from '@material-ui/pickers/constants/prop-types';

import MaskedInput from 'react-text-mask';

import jsonpath from 'jsonpath';
import {
  InputFieldDef as InputFieldProps,
  PickerFieldDef as PickerFieldProps,
  SelectFieldDef as SelectFieldProps,
  AutocompleteFieldDef as AutocompleteFieldProps,
  ResponsiveLayout,
  AnyFieldDef,
  FieldRemoteUpdate,
  FieldUpdateArgs,
} from '../../core/types/form';
import { forLast } from '../../core/utils/performanceUtils';

import Autocomplete from '../autocomplete';
import ConnectedInputField from './ConnectedInputField';

interface ContainerProps {
  layout?: ResponsiveLayout;
  children?: ReactNode | ReactNode[];
}

const defaultLayout: ResponsiveLayout = {
  xs: 12,
};

function isType<T>(value: unknown): value is T {
  return typeof value === 'object';
}

function getMemberOrValue<T>(option: T, member?: string) {
  if (!member) {
    return String(option);
  }

  const optionItem = option as Record<string, unknown>;
  if (typeof optionItem === 'object') {
    const text = jsonpath.value(optionItem, `$.${member}`);
    return text as string || '';
  }

  if (isType<Record<string, unknown>>(option)) {
    return option[member] as string;
  }

  return String(option);
}

function createRemoteUpdateArgs<T>(fieldDef: AnyFieldDef, remoteUpdate: FieldRemoteUpdate<T>) {
  return {
    ...remoteUpdate,
    elementId: fieldDef.fieldKey ? fieldDef.fieldKey : remoteUpdate.elementId,
    fieldLabel: remoteUpdate.fieldLabel ? remoteUpdate.fieldLabel : fieldDef.label,
  } as FieldRemoteUpdate<T>;
}

const Container: React.FC<ContainerProps> = ({
  children,
  layout,
}: ContainerProps) => (
  <Grid
    item
    xs={layout?.xs}
    sm={layout?.sm}
    md={layout?.md}
    lg={layout?.lg}
    xl={layout?.xl}
  >
    {children}
  </Grid>
);

const Blank = <T extends unknown>(props: InputFieldProps<T>) => {
  const {
    layout,
    className,
  } = props;
  return (
    <Container layout={layout || defaultLayout}>
      <Box className={className} />
    </Container>
  );
};

interface TextMaskInputProps extends InputBaseComponentProps {
  inputRef: (ref: HTMLInputElement | null) => void;
  maskExpressions: (string | RegExp)[];
}
const TextMaskInput = (props: TextMaskInputProps) => {
  const { inputRef, maskExpressions, ...others } = props;
  const FieldComponent = MaskedInput;

  return (
    <FieldComponent
      {...others}
      ref={(ref: any) => {
        inputRef(ref ? ref.inputElement : null);
      }}
      mask={maskExpressions}
    />
  );
};

const shouldRemoteUpdate = <T extends unknown>(
  validator: ((newValue: T) => boolean) | undefined,
  newValue: T,
) => {
  if (validator) {
    return validator(newValue);
  }

  return true;
};

const pendingRemoteUpdates: Record<string, unknown> = {};

const useOnChangeHandler = <T extends unknown>(
  props: AnyFieldDef<T>,
) => {
  const {
    updateAction,
    remoteUpdate,
    updatePath,
    updateValueMap,
    onBeforeRemoteUpdate,
    isError,
  } = props;

  // const pendingRemoteUpdateRef = useRef<FieldUpdateArgs<T>>();
  const key = remoteUpdate && `${remoteUpdate.model}[${remoteUpdate.modelId}].${remoteUpdate.modelField}`;
  const pendingRemoteUpdate = key
    && pendingRemoteUpdates[key] as FieldUpdateArgs<unknown> | undefined;

  useEffect(() => {
    if (!isError && pendingRemoteUpdate && updateAction) {
      updateAction(pendingRemoteUpdate);

      if (key) {
        pendingRemoteUpdates[key] = undefined;
      }
    }
  }, [isError, updateAction, pendingRemoteUpdate, key]);

  const handleOnChange = <T2 extends unknown | string | number | undefined>(newValue: T2) => {
    if (!updateAction) {
      return;
    }

    const remoteUpdateArgs = remoteUpdate
      && createRemoteUpdateArgs(props as AnyFieldDef, remoteUpdate);

    if (key) {
      pendingRemoteUpdates[key] = undefined;
    }

    if (remoteUpdate && !shouldRemoteUpdate(onBeforeRemoteUpdate, newValue as unknown as T)) {
      updateAction({
        path: updatePath || '',
        value: updateValueMap ? updateValueMap(newValue as unknown as T) : newValue,
      });

      if (key) {
        pendingRemoteUpdates[key] = {
          path: updatePath || '',
          value: updateValueMap ? updateValueMap(newValue as unknown as T) : newValue,
          remoteUpdate: remoteUpdateArgs,
          remoteValue: remoteUpdate && (
            remoteUpdate?.valueMap
              ? remoteUpdate?.valueMap(newValue as unknown as T)
              : newValue
          ),
        };
      }

      return;
    }

    updateAction({
      path: updatePath || '',
      value: updateValueMap ? updateValueMap(newValue as unknown as T) : newValue,
      remoteUpdate: remoteUpdateArgs,
      remoteValue: remoteUpdate && (
        remoteUpdate?.valueMap
          ? remoteUpdate?.valueMap(newValue as unknown as T)
          : newValue
      ),
    });
  };

  return handleOnChange;
};

const InputField = <T extends unknown>(props: InputFieldProps<T>) => {
  const {
    layout,
    className,
    label,
    isReadOnly,
    isDisabled,
    isRequired,
    value,
    autoFocus,
    componentRef,
    isError,
    errorMessage,
    maskExpressions,
    extraChildren,
  } = props;

  const theme = useTheme();
  const handleOnChange = useOnChangeHandler(props);

  return (
    <Container layout={layout || defaultLayout}>
      <ConnectedInputField
        component={TextField}
        componentProps={{
          className,
          label: (
            <>
              {label}
              {isError && errorMessage && (
                <Tooltip
                  style={{ marginLeft: theme.spacing(0.5) }}
                  title={errorMessage || ''}
                >
                  <ErrorIcon color="error" />
                </Tooltip>
              )}
            </>
          ),
          required: isRequired,
          error: isError,
          disabled: isDisabled,
          InputProps: {
            readOnly: isReadOnly,
            autoFocus,
            inputRef: componentRef,
            inputComponent: maskExpressions && TextMaskInput,
            inputProps: maskExpressions && {
              maskExpressions,
              autoComplete: 'nofill', // Chrome specific.
            },
            autoComplete: 'nofill', // Chrome specific.
            style: {
              marginTop: !label ? 0 : theme.spacing(2),
            },
          },
          InputLabelProps: {
            hidden: !label,
          },
          inputProps: {
            autoComplete: 'nofill', // Chrome specific.
          },
        }}
        value={value as unknown as string || ''}
        onChange={handleOnChange}
      />
      {extraChildren}
    </Container>
  );
};

const SelectField = <T extends unknown>(props: SelectFieldProps<T>) => {
  const {
    layout,
    className,
    label,
    isReadOnly,
    isDisabled,
    isRequired,
    value,
    options,
    valueMember,
    displayMember,
    renderOption,
    autoFocus,
    componentRef,
    isError,
    errorMessage,
    extraChildren,
    disallowEmpty,
  } = props;

  const stringValue = value as unknown as string || '';
  const foundOption = options && options.find((option: T) => (
    getMemberOrValue(option, valueMember) === stringValue
  ));

  const valueOrEmpty = foundOption ? stringValue : '';
  const theme = useTheme();
  const handleOnChange = useOnChangeHandler(props);

  return (
    <Container layout={layout || defaultLayout}>
      <FormControl className={className}>
        {label && (
          <InputLabel>
            {label}
            {isError && errorMessage && (
              <Tooltip
                style={{ marginLeft: theme.spacing(0.5) }}
                title={errorMessage || ''}
              >
                <ErrorIcon color="error" />
              </Tooltip>
            )}
          </InputLabel>
        )}
        <ConnectedInputField
          component={Select}
          componentProps={{
            required: isRequired,
            disabled: isDisabled,
            autoFocus,
            inputRef: componentRef,
            error: isError,
            inputProps: {
              readOnly: isReadOnly,
            },
            children: [
              !disallowEmpty && (
                <MenuItem value=""><em>Select</em></MenuItem>
              ),
              options && options.map(
                (option: T) => (
                  <MenuItem
                    key={getMemberOrValue<T>(option, valueMember)}
                    value={getMemberOrValue<T>(option, valueMember)}
                  >
                    {renderOption
                      ? renderOption(option)
                      : getMemberOrValue<T>(option, displayMember)}
                  </MenuItem>
                ),
              ),
            ],
          }}
          value={valueOrEmpty}
          onChange={handleOnChange}
        />
      </FormControl>
      {extraChildren}
    </Container>
  );
};

const AutocompleteField = <T extends unknown>(props: AutocompleteFieldProps<T>) => {
  const {
    layout,
    className,
    label,
    isReadOnly,
    isDisabled,
    isRequired,
    value,
    options,
    valueMember,
    displayMember,
    renderOption,
    isLoading,
    queryAction,
    cleanUpAction,
    onEnterKeyDown,
    disableClearable,
    getOptionLabel,
    autoFocus,
    componentRef,
    isError,
    errorMessage,
    extraChildren,
  } = props;

  const theme = useTheme();
  const handleOnChange = useOnChangeHandler(props);

  return (
    <Container layout={layout || defaultLayout}>
      <Autocomplete
        textFieldProps={{
          className,
          label: (
            <>
              {label}
              {isError && errorMessage && (
                <Tooltip
                  style={{ marginLeft: theme.spacing(0.5) }}
                  title={errorMessage || ''}
                >
                  <ErrorIcon color="error" />
                </Tooltip>
              )}
            </>
          ),
          disabled: isDisabled,
          InputProps: {
            readOnly: isReadOnly,
            autoComplete: 'off',
            autoFocus,
            inputRef: componentRef,
            disabled: isDisabled,
            style: {
              marginTop: !label ? 0 : theme.spacing(2),
            },
          },
          InputLabelProps: {
            hidden: !label,
          },
          error: isError,
          required: isRequired,
        }}
        open={false}
        options={options || []}
        valueMember={valueMember}
        displayMember={displayMember}
        renderOption={renderOption}
        loading={isLoading}
        onQueryChange={(query) => queryAction && queryAction({ query })}
        onBlur={() => cleanUpAction && cleanUpAction()}
        onEnterKeyDown={onEnterKeyDown}
        value={value}
        disableClearable={disableClearable}
        getOptionLabel={getOptionLabel}
        onChange={handleOnChange}
      />
      {extraChildren}
    </Container>
  );
};

function getPickerComponent(type: string) {
  if (type === 'time') {
    return KeyboardTimePicker;
  }

  if (type === 'date') {
    return KeyboardDatePicker;
  }

  return KeyboardDateTimePicker;
}

const PickerField = <T extends unknown>(props: PickerFieldProps<T>) => {
  const {
    layout,
    className,
    type,
    label,
    isReadOnly,
    isDisabled,
    isRequired,
    value,
    updateAction,
    format,
    mask,
    minDate,
    maxDate,
    autoFocus,
    componentRef,
    keyboardIcon,
    isError,
    errorMessage,
    iconStyle,
    inputStyle,
  } = props;

  const [dateValue, setDateValue] = useState(value);
  const handleOnChange = useOnChangeHandler(props);

  const extractPickerDate = (pickerDate: MaterialUiPickersDate) => {
    if (!pickerDate?.isValid()) {
      return pickerDate?.toDate();
    }

    const creationDataFormat = pickerDate.creationData().format;
    if (creationDataFormat === 'HH' && value) {
      return moment(value as string)
        .hours(pickerDate?.hours()).toDate();
    }

    if (creationDataFormat === 'HH:mm' && value) {
      return moment(value as string)
        .hours(pickerDate?.hours())
        .minutes(pickerDate?.minutes()).toDate();
    }

    if (creationDataFormat === 'HH:mm:ss' && value) {
      return moment(value as string)
        .hours(pickerDate?.hours())
        .minutes(pickerDate?.minutes())
        .seconds(pickerDate?.seconds())
        .toDate();
    }

    if (creationDataFormat === 'MM/DD/YYYY' && value) {
      return moment(value as string)
        .year(pickerDate?.year())
        .month(pickerDate?.month())
        .day(pickerDate?.day())
        .toDate();
    }

    return pickerDate?.toDate();
  };

  useEffect(() => {
    setDateValue(value);
  }, [value]);

  const onInternalChange = async (pickerDate: MaterialUiPickersDate) => {
    const date = pickerDate && extractPickerDate(pickerDate);
    setDateValue(date as T);

    if (updateAction && (pickerDate?.isValid() || date === null)) {
      const finalValue = await forLast(date, 100);

      if (maxDate && pickerDate?.isAfter(maxDate)) {
        return;
      }

      if (minDate && pickerDate?.isBefore(minDate)) {
        return;
      }

      handleOnChange(finalValue);
    }
  };

  const FieldComponent = getPickerComponent(type);
  const theme = useTheme();

  return (
    <Container layout={layout || defaultLayout}>
      <FieldComponent
        className={className}
        variant="inline"
        format={format || 'HH:mm'}
        mask={mask}
        margin="none"
        label={(
          <>
            {label}
            {isError && errorMessage && (
              <Tooltip
                style={{ marginLeft: theme.spacing(0.5) }}
                title={errorMessage || ''}
              >
                <ErrorIcon color="error" fontSize="small" />
              </Tooltip>
            )}
          </>
        )}
        InputLabelProps={{
          hidden: !label,
        }}
        autoOk
        ampm={false}
        minDate={minDate}
        maxDate={maxDate}
        inputRef={componentRef}
        error={isError}
        KeyboardButtonProps={{
          'aria-label': label,
        }}
        FormHelperTextProps={{
          style: { display: 'none' },
        }}
        readOnly={isReadOnly}
        InputProps={{
          readOnly: isReadOnly,
          style: {
            marginTop: !label ? 0 : theme.spacing(2),
          },
        }}
        inputProps={{
          style: inputStyle,
        }}
        InputAdornmentProps={{
          style: iconStyle || {
            marginRight: -theme.spacing(1),
          },
        }}
        keyboardIcon={keyboardIcon}
        disabled={isDisabled}
        required={isRequired}
        autoFocus={autoFocus}
        value={dateValue as ParsableDate || null}
        onChange={(date) => onInternalChange(date)}
        onDoubleClick={() => !isReadOnly && onInternalChange(moment())}
      />
    </Container>
  );
};

const FormField = <T extends unknown>(props: AnyFieldDef) => {
  const { fieldType, layout } = props;

  if (fieldType === 'input') {
    const FieldComponent = InputField;
    return <FieldComponent {...props} />;
  }

  if (fieldType === 'datePicker') {
    const FieldComponent = PickerField;
    const { format } = props as PickerFieldProps<T>;
    return <FieldComponent {...props} type="date" format={format || 'MM/DD/YYYY'} />;
  }

  if (fieldType === 'timePicker') {
    const FieldComponent = PickerField;
    const { format } = props as PickerFieldProps<T>;
    return <FieldComponent {...props} type="time" format={format || 'HH:mm'} />;
  }

  if (fieldType === 'dateTimePicker') {
    const FieldComponent = PickerField;
    const { format } = props as PickerFieldProps<T>;
    return <FieldComponent {...props} type="dateTime" format={format || 'MM/DD/YYYY HH:mm'} />;
  }

  if (fieldType === 'select') {
    const FieldComponent = SelectField;
    return <FieldComponent {...props as SelectFieldProps<T>} />;
  }

  if (fieldType === 'autocomplete') {
    const FieldComponent = AutocompleteField;
    return <FieldComponent {...props as AutocompleteFieldProps<T>} />;
  }

  if (fieldType === 'blank') {
    const FieldComponent = Blank;
    return <FieldComponent {...props} />;
  }

  return <Container layout={layout || defaultLayout} />;
};

export default FormField;
